import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { FileBarChart2, CalendarPlus, Users, Briefcase, Clock, ClipboardList } from "lucide-react";

import logoCollapsed from "../assets/image.jpg";
import logoExpanded from "../assets/logo.jpg";

import { useAuth } from "../context/AuthContext";

import {
  RiDashboardLine,
  RiLogoutBoxRLine,
  RiSettings3Line,
  RiArrowDownSLine,
  RiArrowRightSLine
} from "react-icons/ri";

import { GrAnalytics } from "react-icons/gr";
import { RiCalendarCheckLine } from "react-icons/ri";

import "../styles/sidebar.css";

export default function Sidebar({ sidebarOpen, toggleSidebar }) {
  const [isHovered, setIsHovered] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { instance } = useMsal();

  const { user } = useAuth();

  /* ================= MENU CONFIG ================= */
  const menuItems = [

    {
      name: "My Information",
      icon: <FileBarChart2 size={18} />,
      path: "/team"

    },
    {
      name: "Employee Details",
      icon: <Users size={18} />,
      path: "/employee-master"
    },
    // { name: "Regularization", icon: <FileBarChart2 size={18} />, path: "/employee-request" },
    {
      name: "Timesheet",
      icon: <Clock size={18} />,
      path: "/timesheet"
    },
    ...(user?.role === "ADMIN" ? [{
      name: "All Timesheets",
      icon: <Clock size={18} />,
      path: "/all-timesheets"
    }] : []),

    {
      name: "Leave",
      icon: <RiDashboardLine size={18} />,
      // path: "/dashboard"
      children: [
        { name: " Leave Dashboard", path: "/dashboard" },
        { name: "Leave History", path: "/Leave-History" },

        // ...(user?.role === "ADMIN" 
        //   ? [
        //       { name: "My Team Leave", path: "/MyTeamLeave" },
        //       { name: "Organization Leave", path: "/org-leave" }
        //     ] 
        //   : [])
        ...(user?.role === "MANAGER" || user?.role === "ADMIN"
          ? [{ name: "My Team Leave", path: "/MyTeamLeave" }]
          : []),
        ...(user?.role === "ADMIN"
          ? [{ name: "Organization Leave", path: "/org-leave" }]
          : [])
      ]

    },

    // {
    //   name: "ApplyLeave",
    //   icon: <CalendarPlus />,
    //   path: "/LeaveForm"
    // },
    // Separate Reports for ADMIN

    {
      name: "Project ",
      icon: <Briefcase size={18} />,
      path: "/project-master"
    },
    ...(String(user?.role || "").toUpperCase() === "ADMIN"
      ? [
        {
          name: "Reports",
          icon: <GrAnalytics size={18} />,
          path: "/reports"
        },


      ]
      : [])
  ];





  // Only show Settings if ADMIN
  const adminSettings = user?.role === "ADMIN" ? [
    {
      name: "Settings",
      icon: <RiSettings3Line size={18} />,
      children: [
        { name: "Preferences", path: "/preferences" },
        { name: "Masters", path: "/master" },
      ]


    },
    {
      name: "Audit Trail",
      icon: <ClipboardList size={18} />,
      path: "/audit-trail"
    },
  ] : [];

  const allMenuItems = [...menuItems, ...adminSettings];

  /* ============== AUTO EXPAND ACTIVE PARENT ============== */
  useEffect(() => {
    const newOpenMenus = {};
    allMenuItems.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some(
          (child) => location.pathname === child.path
        );
        if (isChildActive) {
          newOpenMenus[item.name] = true;
        }
      }
    });

    // Only update state when there is an actual change to avoid infinite re-renders
    setOpenMenus((prev) => {
      const keys = Object.keys(newOpenMenus);
      if (keys.length === 0) return prev;
      let changed = false;
      for (const k of keys) {
        if (prev[k] !== newOpenMenus[k]) { changed = true; break; }
      }
      if (!changed) return prev;
      return { ...prev, ...newOpenMenus };
    });
  }, [location.pathname, allMenuItems]);

  useEffect(() => {
    document.body.classList.toggle("sidebar-expanded", isHovered);
  }, [isHovered]);

  const toggleMenu = (menuName) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      await instance.logoutRedirect({
        postLogoutRedirectUri: "/"
      });
    } catch (err) {
      console.error("Logout failed", err);
      navigate("/");
    }
  };

  return (
    <aside
      className={`hover-style hover-sidebar ${isHovered ? "expanded" : ""} ${sidebarOpen ? "mobile-open" : ""
        }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* LOGO */}
      <div className="sidebar-logo-container" onClick={() => navigate("/dashboard")}>
        <img
          src="/apple-touch-icon.png"
          alt="EMS Logo"
          className="sidebar-logo-img"
        />
        <span className="sidebar-brand-text">EMS Portal</span>
      </div>

      <ul className="hover-menu-list">
        {allMenuItems.map((item, index) => {
          const isParentActive = location.pathname === item.path;

          /* ================= PARENT WITH CHILDREN ================= */
          if (item.children) {
            const isOpen = openMenus[item.name];

            return (
              <li key={index}>
                <div
                  className="hover-menu-item settings-parent"
                  onClick={() => toggleMenu(item.name)}
                >
                  <div className="menu-left">
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-text">{item.name}</span>
                  </div>

                  <span className={`menu-arrow ${isOpen ? "rotate" : ""}`}>
                    {isOpen ? <RiArrowDownSLine /> : <RiArrowRightSLine />}
                  </span>
                </div>

                {isOpen && (
                  <ul className="submenu">
                    {item.children.map((child, i) => (
                      <Link
                        key={i}
                        to={child.path}
                        className="hover-menu-link"
                      >
                        <li
                          className={`hover-menu-item submenu-item ${location.pathname === child.path
                            ? "active"
                            : ""
                            }`}
                        >
                          <span className="submenu-dot"></span>
                          <span className="menu-text">
                            {child.name}
                          </span>
                        </li>
                      </Link>
                    ))}
                  </ul>
                )}
              </li>
            );
          }

          /* NORMAL MENU */
          return (
            <Link
              key={index}
              to={item.path}
              className="hover-menu-link"
            >
              <li
                className={`hover-menu-item ${location.pathname === item.path ? "active" : ""
                  }`}
              >
                <span className="menu-icon">{item.icon}</span>
                <span className="menu-text">{item.name}</span>
              </li>
            </Link>
          );
        })}
      </ul>

      {/* LOGOUT */}
      <div className="sidebar-logout">
        <li className="hover-menu-item logout" onClick={handleLogout}>
          <span className="menu-icon">
            <RiLogoutBoxRLine />
          </span>
          <span className="menu-text">Logout</span>
        </li>
      </div>
    </aside>
  );
}
